import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import type { SeoMeta } from "@/types"

type UseCase = {
  slug: string
  title: string
  headline: string
  blurb: string
}

const USE_CASES: UseCase[] = [
  {
    slug: "just-listed-video",
    title: "Just Listed",
    headline: "New instruction, straight to feed",
    blurb:
      "Post a just-listed video within minutes of going live. Compounds portal reach with Instagram, TikTok and Facebook \u2014 and builds your presence in the local market.",
  },
  {
    slug: "price-reduction-video",
    title: "Price Reduction",
    headline: "Reframe a price drop as a buying opportunity",
    blurb:
      "A social ad keeps the reduction in feed for a week after the portal alert fires once. Reassures vendors that the listing is still actively marketed.",
  },
  {
    slug: "open-house-video",
    title: "Open House",
    headline: "Fill every slot before Saturday arrives",
    blurb:
      "A promotable event, not just a listing. Boost the video locally for a week and watch the slots fill \u2014 creating bidding pressure and story for the vendor.",
  },
  {
    slug: "property-walkthrough-video",
    title: "Walkthrough",
    headline: "Show the flow, not just the rooms",
    blurb:
      "A 30-second phone-native walkthrough for every listing \u2014 even the ones that wouldn't justify a videographer. Works with portal photos or real footage.",
  },
  {
    slug: "new-build-launch-video",
    title: "New Build Launch",
    headline: "Phase-specific ads for every release",
    blurb:
      "CGI, lifestyle, housetypes, incentives and availability \u2014 wrapped into a social-first launch video ad. Re-render per phase in seconds.",
  },
]

export default function UseCasesIndex({ meta }: { meta: SeoMeta }) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />

        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={meta.image} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />

        <BreadcrumbSchema canonical={meta.canonical} items={[{ name: "Use cases" }]} />
      </Head>

      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-5xl">
          <div className="max-w-3xl">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
              Use cases
            </p>
            <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl">
              One pipeline, every listing moment
              <span className="text-red">.</span>
            </h1>
            <p className="mt-6 text-lg text-slate">
              QuiteRight isn&rsquo;t one template &mdash; it&rsquo;s a video ad
              for every moment in the life of a listing. Launch it, reduce it,
              open it up, walk through it, phase-release it. Same pipeline,
              different story per moment.
            </p>
          </div>
        </div>
      </section>

      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <div className="grid gap-6 md:grid-cols-2">
            {USE_CASES.map((useCase) => (
              <ScrollReveal key={useCase.slug}>
                <Link
                  href={`/use-cases/${useCase.slug}`}
                  className="group block h-full rounded-xl border border-stone bg-white p-6 transition-colors hover:border-red md:p-8"
                >
                  <p className="text-xs font-medium uppercase tracking-[0.15em] text-red">
                    {useCase.title}
                  </p>
                  <h2 className="mt-3 font-display text-xl font-bold text-charcoal md:text-2xl">
                    {useCase.headline}
                    <span className="text-red">.</span>
                  </h2>
                  <p className="mt-4 text-sm leading-relaxed text-slate">
                    {useCase.blurb}
                  </p>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-red group-hover:gap-3 transition-all">
                    Read more
                    <svg
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      className="size-3.5"
                    >
                      <path d="M6 3l5 5-5 5" />
                    </svg>
                  </span>
                </Link>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-stone px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
            Every moment, every listing
            <span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-lg text-slate">
            Fully managed, unlimited renders, automatic publishing.
            &pound;499/month, no contracts.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/sign-up"
              className="inline-flex w-full items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-red/90 sm:w-auto"
            >
              Start your free trial
            </Link>
            <Link
              href="/demo"
              className="inline-flex w-full items-center justify-center rounded-md border border-stone bg-white px-6 py-3 text-base font-medium text-charcoal transition-colors hover:bg-stone sm:w-auto"
            >
              Book a demo
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  )
}
